import type { AdminFoodListItem } from '@steado/admin-contract/foods';
import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth/auth-context';
import { useDebounced } from '@/lib/use-debounced';
import { foodsQueryOptions } from '@/screens/foods/foods-query';

import { SOURCE_NAMES, figure } from './request-copy';

/**
 * Search the catalog by name and point a request at a result. The catalog
 * list's own endpoint, so it needs no API of its own; by name rather than by
 * pasted id, because copying an id is how the wrong row gets picked.
 */
export function FoodPicker({
  label,
  placeholder,
  initial = '',
  exclude,
  disabled,
  disabledWhy,
  pendingId,
  onPick,
}: {
  readonly label: string;
  readonly placeholder: string;
  readonly initial?: string;
  /** A row never to offer: the request's own estimate. */
  readonly exclude?: string | null;
  readonly disabled: boolean;
  readonly disabledWhy?: string;
  readonly pendingId: string | null;
  readonly onPick: (food: AdminFoodListItem) => void;
}) {
  const { api } = useAuth();
  const [term, setTerm] = useState(initial);
  const settled = useDebounced(term.trim(), 260);
  const inputId = useId();
  const whyId = useId();
  const results = useQuery({
    ...foodsQueryOptions(api, { search: settled, activity: 'active' }),
    enabled: settled.length >= 2,
  });
  const items = (results.data?.items ?? []).filter((food) => food.id !== exclude).slice(0, 6);

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <Input
        id={inputId}
        type="search"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        placeholder={placeholder}
        className="h-[34px] rounded-lg text-[13px]"
      />
      {settled.length < 2 ? null : results.isPending ? (
        <p className="text-xs text-muted-foreground">Searching…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No active food matches that.</p>
      ) : (
        <ul className="overflow-hidden rounded-lg border border-border">
          {items.map((food, index) => (
            <li
              key={food.id}
              className={
                'flex items-center gap-3 px-3 py-1.5' + (index === 0 ? '' : ' border-t border-border')
              }
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[13px] font-medium text-foreground">{food.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {SOURCE_NAMES[food.source]}
                  {food.isVerified ? ', verified' : ''} · {figure(food.macrosPer100g.kcal)} kcal per 100 g
                </span>
              </span>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Point at ${food.name}`}
                aria-disabled={disabled || pendingId !== null}
                aria-describedby={disabled && disabledWhy !== undefined ? whyId : undefined}
                className={disabled || pendingId !== null ? 'cursor-not-allowed opacity-60' : undefined}
                onClick={() => {
                  if (!disabled && pendingId === null) onPick(food);
                }}
              >
                {pendingId === food.id ? 'Pointing…' : 'Point here'}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {disabled && disabledWhy !== undefined ? (
        <span id={whyId} className="text-xs text-muted-foreground">
          {disabledWhy}
        </span>
      ) : null}
    </div>
  );
}
